import { useEffect, useState } from "react";
import { UserCog, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Persistent, unmissable banner shown for the entire duration of a founder
 * impersonation session. Renders NOTHING when no impersonation is active.
 *
 * The banner:
 *   • Displays the target user's email (source of truth for "who am I as").
 *   • Ticks down the time remaining so the founder knows when they'll be
 *     auto-restored (AuthContext also schedules an auto-stop at expiry).
 *   • Offers a one-click "Return to Founder Account" that swaps the token
 *     back and refreshes the workspace.
 */
export default function ImpersonationBanner() {
    const { impersonation, stopImpersonation } = useAuth();
    const [remaining, setRemaining] = useState("");

    useEffect(() => {
        if (!impersonation?.expires_at) { setRemaining(""); return; }
        const compute = () => {
            const diff = Math.max(0, new Date(impersonation.expires_at).getTime() - Date.now());
            const mins = Math.floor(diff / 60000);
            const secs = Math.floor((diff % 60000) / 1000);
            setRemaining(mins > 0 ? `${mins}m ${secs.toString().padStart(2, "0")}s` : `${secs}s`);
        };
        compute();
        const id = setInterval(compute, 1000);
        return () => clearInterval(id);
    }, [impersonation?.expires_at]);

    if (!impersonation) return null;

    const targetEmail = impersonation.target?.email || "unknown user";

    return (
        <div
            data-testid="impersonation-banner"
            role="alert"
            aria-live="polite"
            className="sticky top-0 z-[80] w-full px-3 py-2 flex items-center gap-3 shadow-md"
            style={{
                // Solid warning gradient — deliberately not `card-warm` so
                // the founder can never mistake this for regular chrome.
                background: "linear-gradient(92deg, #B91C1C, #EA580C)",
                color: "white",
                // Clear the iOS Dynamic Island / notch on PWA installs so the
                // banner remains fully legible.
                paddingTop: "max(0.5rem, env(safe-area-inset-top))",
            }}
        >
            <UserCog size={18} className="shrink-0" aria-hidden="true" />
            <div className="flex-1 min-w-0 text-sm leading-tight">
                <div className="font-semibold truncate">
                    Viewing as{" "}
                    <span data-testid="impersonation-target-email" className="font-mono">{targetEmail}</span>
                </div>
                <div className="text-white/85 text-[11px] mt-0.5" data-testid="impersonation-remaining">
                    Founder impersonation · auto-ends in {remaining || "—"}
                </div>
            </div>
            <button
                type="button"
                onClick={stopImpersonation}
                data-testid="impersonation-return-btn"
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 active:bg-white/30 text-xs font-medium transition"
            >
                <LogOut size={13} />
                Return to Founder
            </button>
        </div>
    );
}
